import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { bookmarksApi } from '../api/bookmarks';
import BookmarkItem from '../components/Bookmarks/BookmarkItem';
import CreateSynthesis from '../components/Bookmarks/CreateSynthesis';
import Button from '../components/Common/Button';
import Loading from '../components/Common/Loading';
import ErrorMessage from '../components/Common/ErrorMessage';

/**
 * ProjectBookmarksPage - View and manage project-level bookmarks.
 *
 * Project Sessions: Displays bookmarks created at the series level.
 * Shows all bookmarks across the project grouped by source.
 */
function ProjectBookmarksPage() {
  const { currentProject } = useApp();
  const [bookmarks, setBookmarks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const loadBookmarks = async () => {
    if (!currentProject) {
      setBookmarks([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await bookmarksApi.listAllProject(currentProject.id);
      setBookmarks(data);
    } catch (err) {
      setError(err.message || 'Failed to load bookmarks');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateSynthesis = async (title, content, category) => {
    if (!currentProject) {
      throw new Error('No project selected');
    }

    await bookmarksApi.createProjectSynthesis(currentProject.id, {
      user_title: title,
      content: content,
      category: category
    });

    await loadBookmarks();
    setShowCreateModal(false);
  };

  const handleDelete = async (bookmarkId) => {
    await bookmarksApi.delete(bookmarkId);
    await loadBookmarks();
  };

  useEffect(() => {
    loadBookmarks();
  }, [currentProject?.id]);

  if (!currentProject) {
    return (
      <div className="bookmarks-page">
        <div className="bookmarks-page__empty">
          <p>Select a project to view series bookmarks.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bookmarks-page">
        <Loading text="Loading bookmarks..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bookmarks-page">
        <ErrorMessage message={error} onRetry={loadBookmarks} />
      </div>
    );
  }

  // Group bookmarks by source
  const projectBookmarks = bookmarks.filter(b => b.scope === 'project');
  const storyBookmarks = bookmarks.filter(b => b.scope === 'story');

  return (
    <div className="bookmarks-page">
      <div className="bookmarks-page__header">
        <h1>Series Bookmarks</h1>
        <Button variant="primary" onClick={() => setShowCreateModal(true)}>
          Create Synthesis Note
        </Button>
      </div>

      {bookmarks.length === 0 ? (
        <div className="bookmarks-page__empty">
          <p>No bookmarks yet.</p>
          <p>Bookmark messages from your sessions or create synthesis notes to save important ideas.</p>
        </div>
      ) : (
        <>
          {/* Project-level bookmarks */}
          {projectBookmarks.length > 0 && (
            <div className="bookmarks-section">
              <h2 className="bookmarks-section__title">Series Planning</h2>
              <div className="bookmarks-list">
                {projectBookmarks.map(bookmark => (
                  <BookmarkItem
                    key={bookmark.id}
                    bookmark={bookmark}
                    onDelete={() => handleDelete(bookmark.id)}
                    showScope
                  />
                ))}
              </div>
            </div>
          )}

          {/* Story bookmarks grouped by story */}
          {storyBookmarks.length > 0 && (
            <div className="bookmarks-section">
              <h2 className="bookmarks-section__title">From Stories</h2>
              <div className="bookmarks-list">
                {storyBookmarks.map(bookmark => (
                  <BookmarkItem
                    key={bookmark.id}
                    bookmark={bookmark}
                    onDelete={() => handleDelete(bookmark.id)}
                    showScope
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <CreateSynthesis
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSave={handleCreateSynthesis}
      />
    </div>
  );
}

export default ProjectBookmarksPage;
